export enum FrequencyCapWindow {
  Day = 'DAY',
  Lifetime = 'LIFETIME',
}

export interface AdFrequencyConfig {
  id: string
  frequencyCapImpressions?: number | null
  frequencyCapWindow?: string | null
  rotationWeight?: number | null
}

interface ImpressionRecord {
  count: number
  windowStart: number
  capImpressions?: number | null
  capWindow?: string | null
}

interface AdState {
  impressions: Record<string, ImpressionRecord>
}

const STORAGE_KEY = 'vb_ad_state'

const emptyState = (): AdState => ({ impressions: {} })

const startOfToday = (): number => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

const writeState = (state: AdState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.error(e)
  }
}

const readState = (): AdState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return emptyState()
    const parsed = JSON.parse(raw) as AdState
    const impressions = parsed.impressions ?? {}
    const today = startOfToday()
    const pruned: Record<string, ImpressionRecord> = {}
    for (const [ id, record ] of Object.entries(impressions)) {
      if (record.capWindow === FrequencyCapWindow.Day && record.windowStart < today) continue
      pruned[id] = record
    }
    if (Object.keys(pruned).length !== Object.keys(impressions).length) writeState({ impressions: pruned })
    return { impressions: pruned }
  } catch {
    return emptyState()
  }
}

export const syncAdConfigs = (ads: AdFrequencyConfig[]): void => {
  const state = readState()
  let changed = false
  for (const ad of ads) {
    const existing = state.impressions[ad.id]
    if (existing) {
      const newCapImpressions = ad.frequencyCapImpressions ?? null
      const newCapWindow = ad.frequencyCapWindow ?? null
      if (existing.capImpressions !== newCapImpressions || existing.capWindow !== newCapWindow) {
        state.impressions[ad.id] = { ...existing, capImpressions: newCapImpressions, capWindow: newCapWindow }
        changed = true
      }
    }
  }
  if (changed) writeState(state)
}

export const getExcludedAdIds = (ads?: AdFrequencyConfig[]): string[] => {
  const { impressions } = readState()
  return Object.entries(impressions)
    .filter(([ id, record ]) => {
      const ad = ads?.find((a) => a.id === id)
      const capImpressions = ad?.frequencyCapImpressions ?? record.capImpressions
      const capWindow = ad?.frequencyCapWindow ?? record.capWindow
      return capImpressions && capWindow && record.count >= capImpressions
    })
    .map(([ id ]) => id)
}

export const recordAdImpression = (ad: AdFrequencyConfig): void => {
  const state = readState()
  const existing = state.impressions[ad.id]
  state.impressions[ad.id] = {
    count: (existing?.count ?? 0) + 1,
    windowStart: existing?.windowStart ?? Date.now(),
    capImpressions: ad.frequencyCapImpressions,
    capWindow: ad.frequencyCapWindow,
  }
  writeState(state)
}
